// AppShellHost — the mount of the `material` module (the generation side view,
// phase 2c). The shell edge for one module under the client runtime
// (app/runtime.ts): it mounts the module ONCE in the shell's runtime (the one
// chat and the library live in), so it hears chat's `Signal.WorldChanged` at
// every turn end and re-reads the shown material; its out-Signals (frame,
// toasts, the world re-read) reach their owners through the chat route table
// (useChatSignalRoutes). It projects its Model into React
// (useSyncExternalStore).
//
// It also keeps the module told which material the right panel focuses: the
// panel's focus is frame state, so this bridge turns its changes into
// `material_focused` / `material_unfocused`.

import { useEffect, useState, useSyncExternalStore } from "react";
import type { MaterialRef } from "../../components/Navigation/types.js";
import {
    type MaterialModel,
    type MaterialMsg,
    mkMaterialModule,
} from "../../modules/material.js";
import type { FrameState } from "../frame.js";
import type { AppPorts } from "../ports.js";
import type { Runtime } from "../runtime.js";

export type MaterialSideView = {
    readonly model: MaterialModel;
    readonly send: (msg: MaterialMsg) => void;
};

export type MaterialSideViewDeps = {
    readonly runtime: Runtime;
    readonly ports: AppPorts;
    readonly frame: FrameState;
};

// The material the right panel shows, if any (the chat panel is the focus host).
const panelMaterial = (frame: FrameState): MaterialRef | null =>
    frame.openSecondary === "panel" &&
    frame.panelFocus.type === "entity" &&
    frame.panelFocus.ref.type === "material"
        ? frame.panelFocus.ref
        : null;

export const useMaterialSideView = (
    deps: MaterialSideViewDeps,
): MaterialSideView => {
    const { runtime } = deps;
    const [material] = useState(() =>
        runtime.mount(mkMaterialModule(deps.ports)),
    );
    const model = useSyncExternalStore(runtime.subscribe, material.getModel);

    const send = (msg: MaterialMsg): void => {
        const r = material.dispatch(msg);
        if (r._tag === "Result.Err" && import.meta.env.DEV)
            console.debug("[material] refused", r.error.type, msg.type);
    };

    // Panel focus → module: the panel is the only way a material is shown, so
    // its focus is the module's focus. Re-told if the module went idle while
    // the panel still shows a material.
    // A focus on a course at one of its parts opens that part (bd-lya).
    const focused = panelMaterial(deps.frame);
    const part =
        deps.frame.panelFocus.type === "entity"
            ? deps.frame.panelFocus.part?.id
            : undefined;
    const focusKey = focused === null ? null : focused.id;
    const idle = model.focus._tag === "Focus.None";
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the focused id and part; the title rides along.
    useEffect(() => {
        if (focused === null) send({ type: "material_unfocused" });
        else
            send(
                part === undefined
                    ? { type: "material_focused", root: focused }
                    : { type: "material_focused", root: focused, part },
            );
    }, [focusKey, part, idle]);

    return { model, send };
};
